import { Module, ActionContext } from 'vuex'
import { RootState, MembershipState } from './state_models'
import { LocationMembership } from '../models'
import { IBuilderSettings } from '../models/membership';
import { fetchAndStoreBuilderSettings, fetchAndStoreClientPortalInfo, fetchAndStoreDomainInfo, fetchAndStoreSettings } from '../util/membership_helper';

export const membership: Module<MembershipState, RootState> = {
  namespaced: true,
  state: {
    offers: [],
    products: [],
    locationId: '',
    domainInfo: {},
    clientPortalInfo: {},
    builderSettings: {} as IBuilderSettings,
  } as MembershipState,
  getters: {
    getOffers(state: MembershipState) {
      return [...state.offers];
    },
    getProducts(state: MembershipState) {
      return [...state.products]
    },
    getSettingsData(state: MembershipState) {
      return {
        domainInfo: state.domainInfo,
        clientPortalInfo: state.clientPortalInfo,
        builderSettings: state.builderSettings
      };
    },
  },
  mutations: {
    setOffers (state, offers) {
      state.offers = offers;
    },
    setProducts (state, products) {
      state.products = products;
    },
    setLocationId (state, locId) {
      state.locationId = locId;
    },
    clearAll(state) {
      state.offers = [];
      state.products = [];
    },
    resetSettings(state: MembershipState) {
      state.domainInfo = {};
      state.clientPortalInfo = {};
      state.builderSettings = {} as IBuilderSettings;
    },
    resetDomainInfo(state: MembershipState) {
      state.domainInfo = {}
    },
    resetClientPortalInfo(state: MembershipState) {
      state.clientPortalInfo = {}
    },
    resetBuilderSettings (state: MembershipState) {
      state.builderSettings = {} as IBuilderSettings
    },
    setDomainInfo (state, domainInfo) {
      state.domainInfo = domainInfo
    },
    setClientPortalInfo (state, clientPortalInfo) {
      state.clientPortalInfo = clientPortalInfo
    },
    setBuilderSettings (state: MembershipState, builderSettings: IBuilderSettings) {
      state.builderSettings = builderSettings
    }
  },
  actions: {
    async syncAll(
      context: ActionContext<MembershipState, RootState>,
      params: { locationId: string, forceRefresh?: boolean}
    ) {

      if(!params || !params.locationId) {
        context.commit('clearAll')
        return
      }
      
      const locationId = params.locationId;
      
      if (!params.forceRefresh && context.state.locationId === locationId) return;
      context.commit('setLocationId',params.locationId);
      
      context.commit('clearAll')
      
      LocationMembership.forLocation(locationId)
        .then(membership => {
          if (context.state.locationId === locationId) { // some other call may have changed locationid
            if (membership.offers) context.commit('setOffers', membership.offers.map(a=> ({ key: a.offer_id, value: a.offer_name})));
            if (membership.products) context.commit('setProducts', membership.products.map(a=> ({ key: a.product_id, value: a.product_name})));
          }
        })
        .catch(error => {
          console.warn('Failed to fetch location membership:', error);
          // Continue execution despite the error
        });
    },
    logout: {
      root: true,
      handler: async (context: ActionContext<MembershipState, RootState>) => {
        context.commit('clearAll')
      }
    },
    async syncSettings(
      context: ActionContext<MembershipState, RootState>,
      params: { locationId: string; forceRefresh?: boolean }
    ) {
      if (!params || !params.locationId) {
        context.commit('resetSettings');
        return;
      }
      const locationId = params.locationId;
      if (!params.forceRefresh && context.state.locationId === locationId) return;   
      context.commit('setLocationId',params.locationId); 
      await fetchAndStoreSettings(locationId);
    },
    async syncDomainInfo(
      context: ActionContext<MembershipState, RootState>,
      params: { locationId: string; forceRefresh?: boolean }
    ): Promise<void> {
      if (!params || !params.locationId) {
        context.commit('resetDomainInfo');
        return;
      }
      const locationId = params.locationId;
      if (!params.forceRefresh && context.state.locationId === locationId) return;
      context.commit('setLocationId',params.locationId);
      await fetchAndStoreDomainInfo(locationId);
    },
    async syncClientPortalInfo(
      context: ActionContext<MembershipState, RootState>,
      params: { locationId: string; forceRefresh?: boolean }
    ): Promise<void> {
      if (!params || !params.locationId) {
        context.commit('resetClientPortalInfo');
        return;
      }
      const locationId = params.locationId;
      if (!params.forceRefresh && context.state.locationId === locationId) return;
      context.commit('setLocationId',params.locationId);
      await fetchAndStoreClientPortalInfo(locationId);
    },
    async syncBuilderSettings(
      context: ActionContext<MembershipState, RootState>,
      params: { locationId: string; forceRefresh?: boolean }
    ): Promise<void> {
      if (!params || !params.locationId) {
        context.commit('resetBuilderSettings');
        return;
      }
      const locationId = params.locationId;
      if (!params.forceRefresh && context.state.locationId === locationId) return;
      context.commit('setLocationId',params.locationId);
      await fetchAndStoreBuilderSettings(locationId);
    }
  }
}